import { Checkbox, Dots } from '@cimplico/components';
import { CWTSchemaTable } from '../../../../types/custom-worksheet-v2';
import { sortByOrder } from '../../store/utils';
import { useState } from 'react';
import { addProperty, removeProperty } from '../../store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentFocusProperty } from '../../store/selectors';
import { AnimatePresence, motion } from 'framer-motion';
import Header from './Header';
import Field from './field/Field';

type TableProps = {
    table: CWTSchemaTable;
};

function Table({ table }: TableProps): JSX.Element {
    const dispatch = useDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);

    const currentFocusProperty = useSelector(selectCurrentFocusProperty);
    const isFocused = currentFocusProperty === table.id;

    function addColumn(): void {
        dispatch(addProperty({ parentId: `${table.id}`, type: 'TABLE_COLUMN' }));
    }

    function deleteTable(): void {
        dispatch(removeProperty(table.id));
    }

    return (
        <div className="w-full border border-gray-300 rounded-lg relative">
            {isFocused && <FocusedBorder />}
            {/* Table header */}
            <Header
                property={{
                    id: table.id,
                    name: table.generatedFromFunction ? `${table.name} (Generated)` : table.name,
                    description: table.description,
                    type: table.type,
                }}
                isCollapsed={isCollapsed}
                onCollapse={setIsCollapsed}
                onDelete={deleteTable}
                onCreateColumn={table.generatedFromFunction ? undefined : addColumn}
            />
            {/* Show table container when table is expanded */}
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <motion.div
                        exit={{ height: 0, opacity: 0 }}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ height: { duration: 0.2 } }}
                        className="flex flex-col divide-y divide-gray-300"
                    >
                        {/* Datagrid header */}
                        <div className={`border-t border-gray-300 p-2 flex gap-4 items-center `}>
                            <div className="flex gap-0.5">
                                <Dots className="invisible w-4 h-4 mx-1" />
                                <Checkbox />
                            </div>
                            <p className="w-[200px] pr-1 pl-3 text-xs font-medium text-gray-600">COLUMN</p>
                            <p className="w-[200px] ml-2 px-1 text-xs font-medium text-gray-600">REFERENCE</p>
                            <p className="w-[200px] px-1 text-xs font-medium text-gray-600 grow">TYPE</p>
                        </div>
                        {/* Datagrid table row */}
                        {Object.keys(table.properties.row.properties).length > 0 && (
                            <>
                                {sortByOrder(table.properties.row.properties).map((value) => (
                                    <Field key={value.id} field={value} />
                                ))}
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default Table;

export function FocusedBorder(): JSX.Element {
    return <div className="-z-10 absolute top-0 left-0 w-full h-full border border-indigo-600 rounded-lg bg-indigo-50" />;
}
